import { Link, useLocation } from 'react-router-dom';
import { ArrowUp, Calculator, Gem, Landmark, ShieldAlert, TrendingUp } from 'lucide-react';

/**
 * SiteDisclaimer — single source of truth for the footer that appears
 * at the bottom of every page. Renders:
 *   1) The red "informational only, not financial advice" disclaimer
 *      box (always at the top — must be the most prominent element).
 *   2) Footer line 1: nav links — Privacy Policy · Glossary of Terms
 *      · Back to top (greyed out + unclickable on the current page).
 *   3) Footer line 4: Mega Tier · Accounting Anomalies · True Return Chart.
 *   Contact email + copyright live centered on the last line INSIDE the red box.
 *
 * Accepts an optional `className` so individual pages can add top
 * margin when the content above the disclaimer is a bordered card
 * (which visually shrinks the gap to the disclaimer's red border).
 * Used on /community to match the main-page's ~25px airy gap; default
 * is empty (no extra margin) for pages where content above has no
 * border and the natural rhythm already feels right.
 *
 * Add another nav link by appending an entry to NAV_LINKS.
 */
const CONTACT_EMAIL = 'dividrip@twylightcrow.com';

const NAV_LINKS_PRIMARY = [
  { label: 'Privacy Policy', to: '/privacy-policy' },
  { label: 'Glossary of Terms', to: '/glossary' },
  // Round 98i (Jul 2026) — SEO landing page for founder-led stocks.
  // Every page footer links here so the founder-led page accrues
  // sitewide internal PageRank (helps Google index it faster).
  { label: 'Founder-Led Stocks', to: '/founder-led' },
  // Round 98j (Jul 2026) — SEO landing page for dividend IPO watch.
  { label: 'IPO Watch', to: '/ipo-watch' },
  // DRIP Calculator link lives inside the disclaimer box (contact line).
];

// Sep 2026 — the research hubs get their own row. They were appended to
// the primary row and wrapped mid-list, which left "Covered Call ETF
// List · Back to top" orphaned on a second line. Four rows now, each
// with one job: site pages, research hubs, learn guides, contact.
const NAV_LINKS_RESEARCH = [
  { label: 'Dividends by Sector', to: '/dividend-stocks-by-sector' },
  { label: 'Foreign & ADR Payers', to: '/foreign-dividend-stocks' },
  { label: 'Money Market Yields', to: '/money-market-fund-yields' },
  { label: 'Canadian Payers', to: '/canadian-dividend-stocks' },
  { label: 'Covered Call ETF List', to: '/covered-call-etfs' },
];

// Round 91n — Learn footer row.
// Five short keyword-rich SEO pages (factually audited Round 91m+n).
// Listed in the footer so every page on the site internally links to
// them — internal linking is one of the strongest ranking signals for
// small-content sites, and the footer is the highest-frequency surface.
const LEARN_LINKS = [
  { label: 'What is DRIP?', to: '/learn/what-is-drip' },
  { label: 'Kings vs Aristocrats vs Achievers', to: '/learn/dividend-kings-vs-aristocrats-vs-achievers' },
  { label: 'JEPI vs JEPQ', to: '/learn/jepi-vs-jepq' },
  { label: 'SCHD vs VOO', to: '/learn/schd-vs-voo' },
  { label: 'Covered Call ETFs', to: '/learn/covered-call-etfs' },
];
export default function SiteDisclaimer({ className = '', linksOnly = false }) {
  const { pathname } = useLocation();

  const handleBackToTop = (e) => {
    e.preventDefault();
    // Smooth scroll to absolute top so the full page header is visible
    // again — works on every page (no per-route anchor required).
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  };

  return (
    <div className={`w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8 ${className}`}>
      {/* Disclaimer — kept on top, must remain the most visible element.
          `linksOnly` (landing page) skips the red box and renders the link rows only. */}
      {!linksOnly && <div
        className="rounded-lg border-2 border-red-400 dark:border-red-700 px-5 py-4"
        data-testid="main-disclaimer"
      >
        <p className="text-xs font-bold leading-relaxed text-amber-900 dark:text-yellow-400 text-left">
          Disclaimer: The information provided on this website/service is for informational purposes only and does not constitute financial, investment, or legal advice. Investing in stocks involves high risk, including the loss of principal. We are not responsible for any financial losses or damages resulting from your reliance on this data. Always consult with a qualified financial professional before making investment decisions.
        </p>
        {/* Round 93v (Feb 2026) — Trademark prior-use notice. Sits
            inside the same red disclaimer box so it inherits the same
            "must read" visual weight, but rendered one size smaller
            (text-[11px]) and in a muted gray that still passes WCAG
            contrast on both themes. Disambiguates Twylight Crow's
            tool from the unaffiliated `dividrip.com` competitor. */}
        <p
          className="mt-2 text-[11px] font-bold leading-relaxed text-gray-600 dark:text-gray-400 text-left"
          data-testid="trademark-notice"
        >
          <span className="font-bold">Trademark Notice:</span> Twylight Crow&apos;s Dividend Tracker (formerly indexed as DiviDrip) is an independent portfolio utility. This tool is not affiliated with, authorized, endorsed by, or in any way officially connected to Dividrip.com or its international affiliates.
        </p>
        <p className="mt-3 text-sm font-bold text-center text-muted-foreground" data-testid="disclaimer-contact-line">
          {pathname === '/dividend-reinvestment-calculator' ? (
            <span
              className="block sm:inline text-muted-foreground/60 cursor-default select-none"
              aria-current="page"
              data-testid="footer-drip-calculator-link-current"
            >
              <Calculator className="inline-block w-3.5 h-3.5 mr-1 align-[-2px]" aria-hidden="true" />
              <span className="whitespace-nowrap">Dividend Reinvestment Calculator</span>
            </span>
          ) : (
            <Link
              to="/dividend-reinvestment-calculator"
              className="block sm:inline text-sky-700 dark:text-sky-300 hover:text-sky-800 dark:hover:text-sky-200 underline decoration-transparent decoration-2 underline-offset-4 hover:decoration-current transition-colors"
              data-testid="footer-drip-calculator-link"
            >
              <Calculator className="inline-block w-3.5 h-3.5 mr-1 align-[-2px]" aria-hidden="true" />
              <span className="whitespace-nowrap">Dividend Reinvestment Calculator</span>
            </Link>
          )}
          <span aria-hidden="true" className="mx-2 text-muted-foreground/40 hidden sm:inline">&middot;</span>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="hover:text-sky-700 dark:hover:text-sky-300 underline decoration-transparent decoration-2 underline-offset-4 hover:decoration-current transition-all duration-150"
            data-testid="footer-contact-email"
          >
            <span className="whitespace-nowrap">Contact: {CONTACT_EMAIL}</span>
          </a>
          <span aria-hidden="true" className="mx-2 text-muted-foreground/40 hidden sm:inline">&middot;</span>
          <span className="block sm:inline whitespace-nowrap" data-testid="footer-copyright">© {new Date().getFullYear()} Twylight Crow</span>
        </p>
      </div>}

      {/* Footer line 1 — site pages + back to top */}
      <div
        className={`${linksOnly ? '' : 'mt-3'} flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm font-bold`}
        data-testid="site-footer-nav"
      >
        {NAV_LINKS_PRIMARY.map((link) => (
          <NavItem key={link.to} link={link} pathname={pathname} withSeparator />
        ))}
        {/* Back to top — last item, scrolls window to (0, 0) on every route. */}
        <button
          type="button"
          onClick={handleBackToTop}
          className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 underline decoration-transparent decoration-2 underline-offset-4 hover:decoration-current transition-all duration-150"
          data-testid="footer-back-to-top"
        >
          <ArrowUp className="w-3 h-3" />
          Back to top
        </button>
      </div>

      {/* Footer line 2 — research hubs */}
      <div
        className="mt-1.5 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm font-bold"
        data-testid="site-footer-research"
      >
        {NAV_LINKS_RESEARCH.map((link, idx) => (
          <NavItem
            key={link.to}
            link={link}
            pathname={pathname}
            withSeparator={idx < NAV_LINKS_RESEARCH.length - 1}
          />
        ))}
      </div>

      {/* Footer line 3 — Learn hub links (Round 91n).
          Sits between the nav row and the contact/copyright row per
          user spec. Each link is a Learn SEO page so this row is
          doing double-duty: convenience for human readers AND an
          internal-link boost for the Learn pages on every route. */}
      <div
        className="mt-1.5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm font-bold"
        data-testid="site-footer-learn"
      >
        <span className="text-violet-700/85 dark:text-violet-300/70 font-bold uppercase tracking-[0.18em] text-xs mr-1">
          Learn:
        </span>
        {LEARN_LINKS.map((link, idx) => {
          const isCurrent = pathname === link.to;
          const testId = `footer-learn-${link.to.split('/').pop()}`;
          return (
            <span key={link.to} className="inline-flex items-center gap-x-3">
              {isCurrent ? (
                <span
                  className="text-muted-foreground/60 cursor-default select-none"
                  aria-current="page"
                  data-testid={`${testId}-current`}
                >
                  {link.label}
                </span>
              ) : (
                <Link
                  to={link.to}
                  className="text-muted-foreground hover:text-violet-700 dark:hover:text-violet-300 underline decoration-transparent decoration-2 underline-offset-4 hover:decoration-current transition-all duration-150"
                  data-testid={testId}
                >
                  {link.label}
                </Link>
              )}
              {idx < LEARN_LINKS.length - 1 && (
                <span aria-hidden="true" className="hidden sm:inline text-muted-foreground/40">&middot;</span>
              )}
            </span>
          );
        })}
      </div>

      {/* Footer line 4 — contact + copyright */}
      <div
        className="mt-1.5 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm font-bold text-muted-foreground"
        data-testid="site-footer-meta"
      >
        {pathname === '/mega-tier' ? (
          <span
            className="inline-flex items-center gap-1 text-muted-foreground/60 cursor-default select-none"
            aria-current="page"
            data-testid="footer-mega-tier-link-current"
          >
            <Gem className="w-3.5 h-3.5" aria-hidden="true" /> Mega Tier Undervalued Stocks
          </span>
        ) : (
          <Link
            to="/mega-tier"
            className="inline-flex items-center gap-1 text-purple-700 dark:text-violet-300 hover:text-purple-800 dark:hover:text-violet-200 underline decoration-transparent decoration-2 underline-offset-4 hover:decoration-current transition-colors"
            data-testid="footer-mega-tier-link"
          >
            <Gem className="w-3.5 h-3.5" aria-hidden="true" /> Mega Tier Undervalued Stocks
          </Link>
        )}
        <span aria-hidden="true" className="hidden sm:inline text-muted-foreground/40">&middot;</span>
        {pathname === '/accounting-anomalies' ? (
          <span className="inline-flex items-center gap-1 text-muted-foreground/60 cursor-default select-none" aria-current="page" data-testid="footer-anomalies-link-current">
            <ShieldAlert className="w-3.5 h-3.5" aria-hidden="true" /> Accounting Anomalies by Sector
          </span>
        ) : (
          <Link
            to="/accounting-anomalies"
            className="inline-flex items-center gap-1 text-fuchsia-700 dark:text-fuchsia-300 hover:text-fuchsia-800 dark:hover:text-fuchsia-200 underline decoration-transparent decoration-2 underline-offset-4 hover:decoration-current transition-colors"
            data-testid="footer-anomalies-link"
          >
            <ShieldAlert className="w-3.5 h-3.5" aria-hidden="true" /> Accounting Anomalies by Sector
          </Link>
        )}
        <span aria-hidden="true" className="hidden sm:inline text-muted-foreground/40">&middot;</span>
        {pathname === '/buffett-test' ? (
          <span className="inline-flex items-center gap-1 text-muted-foreground/60 cursor-default select-none" aria-current="page" data-testid="footer-buffett-link-current">
            <Landmark className="w-3.5 h-3.5" aria-hidden="true" /> Buffett $1 Retained Earnings
          </span>
        ) : (
          <Link
            to="/buffett-test"
            className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-300 hover:text-amber-800 dark:hover:text-amber-200 underline decoration-transparent decoration-2 underline-offset-4 hover:decoration-current transition-colors"
            data-testid="footer-buffett-link"
          >
            <Landmark className="w-3.5 h-3.5" aria-hidden="true" /> Buffett $1 Retained Earnings
          </Link>
        )}
        <span aria-hidden="true" className="hidden sm:inline text-muted-foreground/40">&middot;</span>
        {pathname.startsWith('/true-return') ? (
          <span className="inline-flex items-center gap-1 text-muted-foreground/60 cursor-default select-none" aria-current="page" data-testid="footer-true-return-link-current">
            <TrendingUp className="w-3.5 h-3.5" aria-hidden="true" /> True Return Chart
          </span>
        ) : (
          <Link
            to="/true-return"
            className="inline-flex items-center gap-1 text-sky-700 dark:text-sky-300 hover:text-sky-800 dark:hover:text-sky-200 underline decoration-transparent decoration-2 underline-offset-4 hover:decoration-current transition-colors"
            data-testid="footer-true-return-link"
          >
            <TrendingUp className="w-3.5 h-3.5" aria-hidden="true" /> True Return Chart
          </Link>
        )}
      </div>
    </div>
  );
}

/** One footer link: greyed out and unclickable when it is the current page. */
function NavItem({ link, pathname, withSeparator }) {
  const isCurrent = pathname === link.to;
  const testId = `footer-nav-${link.to.replace(/\//g, '')}`;
  return (
    <span className="inline-flex items-center gap-x-4">
      {isCurrent ? (
        <span
          className="text-muted-foreground/60 cursor-default select-none"
          aria-current="page"
          data-testid={`${testId}-current`}
        >
          {link.label}
        </span>
      ) : (
        <Link
          to={link.to}
          className="text-muted-foreground hover:text-sky-700 dark:hover:text-sky-300 underline decoration-transparent decoration-2 underline-offset-4 hover:decoration-current transition-all duration-150"
          data-testid={testId}
        >
          {link.label}
        </Link>
      )}
      {withSeparator && (
        // Hidden below sm: when the row wraps on a phone the dots end up
        // dangling at line ends. The gap alone separates them there.
        <span aria-hidden="true" className="hidden sm:inline text-muted-foreground/40">&middot;</span>
      )}
    </span>
  );
}
